import { Auth, Connect, Message } from '@genesislcap/foundation-comms';
import { DI } from '@microsoft/fast-foundation';
import { CONTRACT_TYPE, PRODUCT_TYPE } from '../utils/enums/generic.enum';
import { ICompanyService } from './company.service';
import { logger } from '@genesislcap/foundation-comms';

export enum CASCADE_ENUM {
  CONTRACT_TYPE = 'CONTRACT_TYPE',
  NEGATIVE_PRICING_METHOD = 'NEGATIVE_PRICING_METHOD',
  RFQ_NEGATIVE_PRICING_METHOD = 'RFQ_NEGATIVE_PRICING_METHOD',
  ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHOD = 'ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHOD',
  REC_DELIVERY_METHODOLOGY = 'REC_DELIVERY_METHODOLOGY',
  RFQ_REC_DELIVERY_METHODOLOGY = 'RFQ_REC_DELIVERY_METHODOLOGY',
  PERFORMANCE_GUARANTEE_METHOD = 'PERFORMANCE_GUARANTEE_METHOD',
  RFQ_PERFORMANCE_GUARANTEE_METHOD = 'RFQ_PERFORMANCE_GUARANTEE_METHOD',
  APPLICABLE_REC_PROGRAM = 'APPLICABLE_REC_PROGRAM',
  RFQ_APPLICABLE_REC_PROGRAM = 'RFQ_APPLICABLE_REC_PROGRAM',
  RFQ_CREDIT_PROFILE = 'RFQ_CREDIT_PROFILE',
  TECHNOLOGY = 'TECHNOLOGY',
  NEGOTIATION_STATUS = 'NEGOTIATION_STATUS',
  PANEL_TECHNOLOGY = 'PANEL_TECHNOLOGY',
  PANEL_TYPE = 'PANEL_TYPE',
  TRACKING_TYPE = 'TRACKING_TYPE',
  PROJECT_DRAFT_STATUS = 'PROJECT_DRAFT_STATUS',
  PROJECT_SUBMISSION_STATUS = 'PROJECT_SUBMISSION_STATUS',
  INTERCONNECTION_VOLTAGE = 'INTERCONNECTION_VOLTAGE',
  CREDIT_PROFILE = 'CREDIT_PROFILE',
  VPPA_CREDIT_PROFILE = 'VPPA_CREDIT_PROFILE',
  SETTLEMENT_INDEX = 'SETTLEMENT_INDEX',
  VPPA_STATUS = 'VPPA_STATUS',
  RFQ_STATUS = 'RFQ_STATUS',
  PROJECT_STATUS = 'PROJECT_STATUS',
  APPLICABLE_TRACKING_SYSTEM = 'APPLICABLE_TRACKING_SYSTEM',
  COMPANY_ROLE = 'COMPANY_ROLE',
  REGULATORY_STATUS = 'REGULATORY_STATUS',
  PRODUCT_TYPE = 'PRODUCT_TYPE',
  BUYER_UPSIDE_ALLOCATION = 'BUYER_UPSIDE_ALLOCATION',
  FORM_AGREEMENT_TYPE = 'FORM_AGREEMENT_TYPE',
  RESURETY_REMAP_VERSION = 'RESURETY_REMAP_VERSION',
  RESURETY_REMAP_TRIAL_STATUS = 'RESURETY_REMAP_TRIAL_STATUS',
  DOCUMENT_STATUS_DEFAULT = 'NOT_SELECTED',
}

const nonSefParticipantRestriction: Record<CASCADE_ENUM, string[] | undefined> = {} as Record<
  CASCADE_ENUM,
  string[] | undefined
>;

nonSefParticipantRestriction[CASCADE_ENUM.PRODUCT_TYPE] = [PRODUCT_TYPE.VIRTUAL_PPA];
nonSefParticipantRestriction[CASCADE_ENUM.CONTRACT_TYPE] = [
  CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED,
  CONTRACT_TYPE.NEW_BUILD_VPPA,
  CONTRACT_TYPE.POST_BUILD_VPPA,
];

export { nonSefParticipantRestriction };

export interface EnumTranslate {
  ENUM_VALUE: string;
  READABLE_VALUE: string;
}

export interface IEnumService {
  getEnumValues(enumKey: CASCADE_ENUM): Promise<Message<any>>;
  getFilteredEnumValues(enumKey: CASCADE_ENUM): Promise<Message<any>>;
  getEnumTranslation(enumKey: CASCADE_ENUM, enumValue: string): string;
  isDocumentEnumSelected(value: String): boolean;
}

class EnumServiceImpl implements IEnumService {
  private enumMap = new Map<CASCADE_ENUM, Array<EnumTranslate>>();
  @ICompanyService companyService: ICompanyService;

  constructor(
    @Connect private connect: Connect,
    @Auth private auth: Auth,
  ) {
    this.loadEnums();
  }

  isDocumentEnumSelected(value: String): boolean {
    return value && value != CASCADE_ENUM.DOCUMENT_STATUS_DEFAULT;
  }

  private loadEnums() {
    const enumValues = Object.values(CASCADE_ENUM);
    enumValues.forEach((element) => {
      this.loadSpecificEnum(element);
    });
  }

  private loadSpecificEnum(enumValue: CASCADE_ENUM) {
    this.connect.request(enumValue, { REQUEST: true }).then(
      (response) => {
        this.enumMap.set(enumValue, response.REPLY);
      },
      (error) => {
        logger.error(error);
      },
    );
  }

  private async lazyLoadSpecificEnum(enumValue: CASCADE_ENUM) {
    try {
      const response = await this.connect.request(enumValue, { REQUEST: true });
      if (response) {
        this.enumMap.set(enumValue, response.REPLY);
      }
    } catch (error) {}
  }

  async getEnumValues(enumKey: CASCADE_ENUM): Promise<Message<any>> {
    if (!this.enumMap.get(enumKey)) {
      return this.connect.request(enumKey, { REQUEST: true });
    }
    const message: Message<any> = {
      REPLY: this.enumMap.get(enumKey),
    };
    return new Promise((resolve) => {
      resolve(message);
    });
  }

  async getFilteredEnumValues(enumKey: CASCADE_ENUM): Promise<Message<any>> {
    if (this.companyService.isSefParticipantAgreement()) {
      return this.getEnumValues(enumKey);
    }

    if (!this.enumMap.get(enumKey)) {
      await this.lazyLoadSpecificEnum(enumKey);
    }

    let enumValues = this.enumMap.get(enumKey);

    if (nonSefParticipantRestriction[enumKey]) {
      enumValues = enumValues.filter((elm) => {
        return (
          !nonSefParticipantRestriction[enumKey] ||
          !nonSefParticipantRestriction[enumKey].includes(elm.ENUM_VALUE)
        );
      });
    }

    const message: Message<any> = {
      REPLY: enumValues,
    };
    return new Promise((resolve) => {
      resolve(message);
    });
  }

  getEnumTranslation(enumKey: CASCADE_ENUM, enumValue: string, defaultValue?: string): string {
    if (!enumValue) {
      return defaultValue ? defaultValue : '';
    }

    if (!this.enumMap.get(enumKey)) {
      return enumValue;
    }

    const enumObject = this.enumMap.get(enumKey).find((elm) => elm.ENUM_VALUE === enumValue);
    return enumObject ? enumObject.READABLE_VALUE : enumValue;
  }
}

export const IEnumService = DI.createInterface<IEnumService>((x) => x.singleton(EnumServiceImpl));
